import { useMemo } from "react";

import { StructuredSearchProvider } from "@/components/StructuredSearch";
import type { HistoryImportConfig } from "@/components/ui/ag-grid-table";
import { useGetImportedSourceIds } from "@/queries/smart-tables";
import type { SmartTableRequestImportPayload } from "@/queries/smart-tables";
import RequestLogsAgGrid from "@/routes/analytics/RequestLogsAgGrid";
import { buildHistoryImportExclusionLockedFilters } from "@/routes/smart-table/utils/buildHistoryImportExclusionLockedFilters";
import { HistoryImportSource } from "@/routes/smart-table/utils/historyImportTypes";

interface RequestHistoryImportContentProps {
  importConfig: HistoryImportConfig<SmartTableRequestImportPayload>;
  tableId?: string;
  sheetId?: string;
  targetColumnId?: string | null;
}

const RequestHistoryImportContent = ({
  importConfig,
  tableId,
  sheetId,
  targetColumnId,
}: RequestHistoryImportContentProps) => {
  const { data: importedIds = [], isLoading } = useGetImportedSourceIds({
    tableId,
    sheetId,
    source: "request",
    columnId: targetColumnId,
    enabled: Boolean(tableId && sheetId),
  });

  const exclusionFilters = useMemo(
    () =>
      buildHistoryImportExclusionLockedFilters({
        source: HistoryImportSource.Request,
        importedIds,
      }),
    [importedIds],
  );

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
        Loading imported requests…
      </div>
    );
  }

  const providerKey = [
    "request-import",
    targetColumnId ?? "sheet",
    importedIds.length,
    importedIds[0] ?? "",
    importedIds[importedIds.length - 1] ?? "",
  ].join(":");

  return (
    <StructuredSearchProvider
      key={providerKey}
      initialEditableFilters={exclusionFilters}
      initialUrlHydrationReady
    >
      <RequestLogsAgGrid
        syncUrl={false}
        searchTitle="Requests"
        requestImportConfig={importConfig}
        useExternalStructuredSearchProvider
      />
    </StructuredSearchProvider>
  );
};

export default RequestHistoryImportContent;
